import type {
  AddOverlayResponse,
  BudgetResponse,
  ConfigResponse,
  FlightDetailResponse,
  FlightListResponse,
  HeartbeatRequest,
  LoginRequest,
  LoginResponse,
  MeResponse,
  OverlayListResponse,
  OverlayEntry,
  PanoramasResponse,
  PresenceCounts,
  StatusResponse,
  TeamsListResponse,
  UsageResponse,
  UsageTreesResponse,
} from "./types";

export const OVERLAY_CONTENT_TYPE = "application/vnd.google-earth.kmz";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** The parsed error body, for callers that need more than the code
     *  (an overlay id collision names the overlay it clashed with). */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class AuthError extends ApiError {
  constructor(code: string, message: string) {
    super(401, code, message);
    this.name = "AuthError";
  }
}

export class PausedError extends ApiError {
  constructor(public readonly since: string | null) {
    super(503, "service_paused", "Atlas is paused");
    this.name = "PausedError";
  }
}

export interface ApiClientOpts {
  baseUrl: string;
  getToken: () => string | null;
  /** `reason` is the Worker's machine-readable cause when it gave one — today
   *  only `"signed_in_elsewhere"`, which the login page turns into a sentence. */
  onUnauthorized?: (reason?: string) => void;
  onPaused?: (since: string | null) => void;
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOpts) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = this.opts.getToken();
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...((init.headers as Record<string, string> | undefined) ?? {}),
    };
    if (token !== null) headers.Authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${this.opts.baseUrl}${path}`, { ...init, headers });
    } catch (err) {
      throw new ApiError(0, "network_error", (err as Error).message);
    }

    let body: unknown = null;
    const text = await res.text();
    if (text.length > 0) {
      try {
        body = JSON.parse(text);
      } catch {
        body = { error: "invalid_json", message: text.slice(0, 200) };
      }
    }

    if (res.status === 503 && (body as { error?: string } | null)?.error === "service_paused") {
      const since = (body as { since?: string } | null)?.since ?? null;
      this.opts.onPaused?.(since);
      throw new PausedError(since);
    }
    if (res.status === 401) {
      const reason = (body as { reason?: unknown } | null)?.reason;
      this.opts.onUnauthorized?.(typeof reason === "string" ? reason : undefined);
      const code = (body as { error?: string } | null)?.error ?? "unauthorized";
      const msg = (body as { message?: string } | null)?.message ?? "unauthorized";
      throw new AuthError(code, msg);
    }
    if (!res.ok) {
      const code = (body as { error?: string } | null)?.error ?? `http_${res.status}`;
      const msg = (body as { message?: string } | null)?.message ?? res.statusText;
      throw new ApiError(res.status, code, msg, body);
    }
    return body as T;
  }

  login(req: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(req),
    });
  }

  logout(): Promise<{ ok: true }> {
    return this.request<{ ok: true }>("/api/auth/logout", { method: "POST" });
  }

  me(): Promise<MeResponse> {
    return this.request<MeResponse>("/api/auth/me");
  }

  /** One beat of seat presence (spec §5.4). Failures are the caller's to
   *  swallow: a missed minute costs nothing, the next one retries. */
  heartbeat(body: HeartbeatRequest): Promise<PresenceCounts> {
    return this.request<PresenceCounts>("/api/presence/heartbeat", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  getStatus(): Promise<StatusResponse> {
    return this.request<StatusResponse>("/api/status");
  }

  getConfig(): Promise<ConfigResponse> {
    return this.request<ConfigResponse>("/api/config");
  }

  /** Spec §9: the caller's tree (admin: `team` picks any team; a sub-client
   *  resolves to its root). A leaf team gets a 403 `not_a_parent`. */
  getUsage(opts: { month?: string; team?: string } = {}): Promise<UsageResponse> {
    const q = new URLSearchParams();
    if (opts.month !== undefined) q.set("month", opts.month);
    if (opts.team !== undefined) q.set("team", opts.team);
    const qs = q.toString();
    return this.request<UsageResponse>(`/api/usage${qs.length > 0 ? `?${qs}` : ""}`);
  }

  /** Admin only: every root tree's month by tiles, people under each. */
  getUsageTrees(opts: { month?: string } = {}): Promise<UsageTreesResponse> {
    const qs = opts.month === undefined ? "" : `?month=${encodeURIComponent(opts.month)}`;
    return this.request<UsageTreesResponse>(`/api/usage/trees${qs}`);
  }

  /** Admin only: the budget month record, the pause flag, the enforcement flag. */
  getBudget(opts: { month?: string } = {}): Promise<BudgetResponse> {
    const qs = opts.month === undefined ? "" : `?month=${encodeURIComponent(opts.month)}`;
    return this.request<BudgetResponse>(`/api/budget${qs}`);
  }

  listFlights(
    opts: { cursor?: string; limit?: number; team?: string } = {},
  ): Promise<FlightListResponse> {
    const qs = new URLSearchParams();
    if (opts.cursor !== undefined) qs.set("cursor", opts.cursor);
    if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
    if (opts.team !== undefined) qs.set("team", opts.team);
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return this.request<FlightListResponse>(`/api/flights${suffix}`);
  }

  getFlight(id: string, opts: { team?: string } = {}): Promise<FlightDetailResponse> {
    const qs = new URLSearchParams();
    if (opts.team !== undefined) qs.set("team", opts.team);
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return this.request<FlightDetailResponse>(
      `/api/flights/${encodeURIComponent(id)}${suffix}`,
    );
  }

  getFlightFootprintUrl(flightId: string, opts: { team?: string } = {}): string {
    const params = new URLSearchParams();
    if (opts.team !== undefined) params.set("team", opts.team);
    const q = params.toString();
    const path = `/api/flights/${encodeURIComponent(flightId)}/footprint`;
    return `${this.opts.baseUrl}${path}${q ? `?${q}` : ""}`;
  }

  listTeams(): Promise<TeamsListResponse> {
    return this.request<TeamsListResponse>("/api/teams");
  }

  async getPanoramas(
    id: string,
    opts: { team?: string } = {},
  ): Promise<PanoramasResponse | null> {
    const qs = new URLSearchParams();
    if (opts.team !== undefined) qs.set("team", opts.team);
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    try {
      return await this.request<PanoramasResponse>(
        `/api/flights/${encodeURIComponent(id)}/panoramas${suffix}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  /** List the team's reference overlays (id + name). */
  listOverlays(team?: string): Promise<OverlayListResponse> {
    const qs = team !== undefined ? `?team=${encodeURIComponent(team)}` : "";
    return this.request<OverlayListResponse>(`/api/overlays${qs}`);
  }

  /** Add (or replace by name) a team overlay from the website (spec 2026-09-09).
   *  The Worker derives the id and returns the manifest entry it wrote. */
  async addOverlay(kmz: Blob, name: string, team?: string): Promise<OverlayEntry> {
    let qs = `?name=${encodeURIComponent(name)}`;
    if (team !== undefined) qs += `&team=${encodeURIComponent(team)}`;
    const res = await this.request<AddOverlayResponse>(`/api/overlays${qs}`, {
      method: "POST",
      headers: { "content-type": OVERLAY_CONTENT_TYPE },
      body: kmz,
    });
    return res.overlay;
  }

  /** Remove one of the team's own overlays. An inherited id is a 404. */
  async removeOverlay(id: string, team?: string): Promise<void> {
    const qs = team !== undefined ? `?team=${encodeURIComponent(team)}` : "";
    await this.request<{ ok: true }>(`/api/overlays/${encodeURIComponent(id)}${qs}`, {
      method: "DELETE",
    });
  }

  /** Fetch one overlay's KMZ bytes, or null if absent. */
  async getOverlayBytes(id: string, team?: string): Promise<ArrayBuffer | null> {
    const qs = team !== undefined ? `?team=${encodeURIComponent(team)}` : "";
    const res = await this.rawRequest(`/api/overlays/${encodeURIComponent(id)}${qs}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, `http_${res.status}`, res.statusText);
    return res.arrayBuffer();
  }

  async rawRequest(
    path: string,
    init: RequestInit & { ignoreUnauthorized?: boolean } = {},
  ): Promise<Response> {
    const token = this.opts.getToken();
    const headers = new Headers(init.headers);
    if (token !== null) headers.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
      res = await fetch(`${this.opts.baseUrl}${path}`, { ...init, headers });
    } catch (err) {
      throw new ApiError(0, "network_error", (err as Error).message);
    }
    if (res.status === 503) {
      const clone = res.clone();
      const body = (await clone.json().catch(() => null)) as
        | { error?: string; since?: string }
        | null;
      if (body?.error === "service_paused") {
        this.opts.onPaused?.(body.since ?? null);
        throw new PausedError(body.since ?? null);
      }
    }
    if (res.status === 401 && init.ignoreUnauthorized !== true) {
      // Read off a clone so the caller still gets an untouched response; a
      // 401 that is not JSON simply has no reason to give.
      const body = (await res.clone().json().catch(() => null)) as { reason?: unknown } | null;
      const reason = body?.reason;
      this.opts.onUnauthorized?.(typeof reason === "string" ? reason : undefined);
      throw new AuthError("unauthorized", "unauthorized");
    }
    return res;
  }
}
